import { useCallback, useState, useMemo } from 'react'
import { useWireState, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { ACCOUNT_TYPES } from '@constants/purchases'
import { useStripe } from '@stripe/react-stripe-js'
import { toast } from 'react-toastify'

const PurchaseCreditsDialogViewModel = () => {
    
    const stripe = useStripe()
    
    const [loading, setLoading] = useState(false)
    const [amount, setAmount] = useState(50)
    const [selectedCardId, setSelectedCardId] = useState(null)
    const [selectedAccountType, setSelectedAccountType] = useState(ACCOUNT_TYPES.Organization)
    
    const credits = useWireValue(store.credits)
    const currentOrganization = useWireValue(store.currentOrganization)
    const [open, setOpen] = useWireState(store.purchaseCreditsDialogOpen)
    const remoteConfig = useWireValue(store.remoteConfig)
    
    const closeDialog = () => setOpen(false)
    
    const updateAccountBalances = useCallback(async () => {
        
        const prevCredits = { ...credits }
        const nextCredits = await actions.updateLocalCredits()
        
        if (prevCredits.user !== nextCredits.user || prevCredits.organization !== nextCredits.organization)
            toast.success('Account balance updated')
        
    }, [])
    
    const currBalance = useMemo(() => (credits[selectedAccountType]  || 0), [credits, selectedAccountType])
    
    const handleSubmit = useCallback(async () => {
        
        setLoading(true)
        
        try {
            
            const { clientSecret } = await actions
                .createStripePaymentIntent(selectedAccountType, amount, 'usd')
            
            if (!clientSecret?.length)
                throw new Error('Stripe Payment Intent clientSecret is empty')
            
            const res = await stripe.confirmCardPayment(clientSecret, {
                payment_method: selectedCardId,
            })
            
            if (res?.error)
                return console.error('Error charging:', res?.error)
            
            toast.success(
                <div>
                    <p>Purchase successful</p>
                    <p className="text-xs">
                        Your balance will be updated shortly
                    </p>
                </div>,
            )
            
            // Since we don't have 2-way communication with the
            // server yet, schedule a refresh of the user's account
            if (!remoteConfig?.realtimeEnabled) setTimeout(updateAccountBalances, 3000)
            
        } catch (e) {
            
            console.error('Error charging:', e)
            toast.error('Failed to purchase credits, please try again')
            
        }
        
        setLoading(false)
        setOpen(false)
        
    }, [selectedAccountType, amount, selectedCardId])
    
    return {
        
        // Constants
        ACCOUNT_TYPES,
        
        // State
        loading,
        setLoading,
        amount,
        setAmount,
        selectedCardId,
        setSelectedCardId,
        selectedAccountType,
        setSelectedAccountType,
        currBalance,
        
        // Global State
        open,
        setOpen,
        currentOrganization,
        
        // Methods
        closeDialog,
        handleSubmit,
        
    }
    
}

export default PurchaseCreditsDialogViewModel
